import React from 'react';
import { Box, Divider, useTheme, Skeleton, styled } from '@mui/material';
import { makeStyles } from '@mui/styles';

import DescTooltip from '../../../components/DescTooltip';
import { VerticalCFRangeGraph } from '../../../components/graphs/VerticalCFRangeGraph';
import VerticalCFRangeGraphLegend from '../../../components/graphs/VerticalCFRangeGraphLegend';

// Add the prop types for the chart
export interface CowbellFactorChartData {
  min: number;
  max: number;
  key: string;
  label: string;
  tooltip: any;
  company: number;
  industry: number;
}

export interface CowbellFactorsChartsProps {
  data?: CowbellFactorChartData[];
  isLoading?: boolean;
  riskDate?: any;
  title?: string;
  aggregateLabel?: string;
}

export const CowbellFactorsCharts = ({
  data = [],
  isLoading = false,
  riskDate = '',
  title = 'Cowbell Factors (CFs)',
  aggregateLabel = 'Company CF',
}: CowbellFactorsChartsProps) => {
  const classes = useGeneralClasses();
  useTheme();

  if (isLoading) {
    return (
      <>
        <Box className={classes.headingContainer}>
          <section>
            <StyledHeading>{title}</StyledHeading>
            <span>{riskDate.ui_dateText}</span>
          </section>
          <div style={{ height: '2.5rem' }}>
            <VerticalCFRangeGraphLegend aggregateLabel={aggregateLabel} />
          </div>
        </Box>
        <Divider
          sx={{ borderColor: (theme) => theme.palette.common.textActionHover }}
        />
        <div className={classes.loadingContainer}>
          {Array.from(new Array(9))
            .fill(null)
            .map((v, index) => (
              <Box
                className={classes.loadingItem}
                mx={1}
                key={`skel-factors-${index}`}
              >
                <Skeleton variant="rectangular" width="0.5rem" height="8rem" />
                <Skeleton
                  variant="text"
                  width={60}
                  height={20}
                  style={{ marginTop: 5 }}
                />
              </Box>
            ))}
        </div>
      </>
    );
  }

  return (
    <>
      <div className={classes.headingContainer}>
        <section>
          <StyledHeading>{title}</StyledHeading>
          <span>{riskDate.ui_dateText}</span>
        </section>
        <div style={{ height: '2.5rem' }}>
          <VerticalCFRangeGraphLegend aggregateLabel={aggregateLabel} />
        </div>
      </div>
      <Divider
        sx={{ borderColor: (theme) => theme.palette.common.textActionHover }}
      />
      <Box className={classes.chartContainer}>
        {data.map((factor) => {
          return (
            <section className={classes.container} key={factor.label}>
              <Box style={{ height: '10rem' }}>
                <VerticalCFRangeGraph
                  min={factor.min}
                  max={factor.max}
                  factor={factor.company}
                  average={factor.industry}
                />
              </Box>
              <Box
                className="contrast-text"
                minWidth="3rem"
                paddingTop={1}
                sx={{ fontSize: '0.667rem' }}
              >
                {factor.label} <DescTooltip tooltip={factor.tooltip} />
              </Box>
            </section>
          );
        })}
      </Box>
    </>
  );
};

const useGeneralClasses = makeStyles(({ breakpoints }) => ({
  loadingContainer: {
    display: 'flex',
    justifyContent: 'space-around',
    paddingTop: '1rem',
  },
  container: {
    textAlign: 'center',
    marginBottom: '1.5rem',
  },
  headingContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: '0.5rem',
  },
  loadingItem: {
    height: '10rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  chartContainer: {
    marginTop: '1rem',
    minHeight: '10rem',

    [breakpoints.up('lg')]: {
      display: 'flex',
      justifyContent: 'space-around',
    },
    alignItems: 'center',
    justifyContent: 'start',
    gap: '0.3rem',
    display: 'flex',
    flexWrap: 'wrap',
  },
  legend: {
    display: 'flex',
    paddingBottom: 0,
    gap: '1rem',
    '& > *': {
      gap: '0.5rem',
    },
  },
}));

const StyledHeading = styled('h3')(({ theme }) => ({
  fontWeight: 600,
  fontSize: '1.333rem',
  lineHeight: '2rem',
  color:
    theme.palette.mode === 'dark'
      ? theme.palette.common.surfaceDisabled
      : theme.palette.common.gunmetal,
  margin: 0,
}));
